import { HttpErrorResponse } from '@angular/common/http';
import axios, { AxiosError } from 'axios';
import { ErrorBase } from 'src/app/core/interfaces/error-base';

export class ErrStatus {
  message: string = '';
  code: number = 0;
  status: string = '';
  reason: string = '';
}

export enum ApiErrorType {
  tokenExpired = 'MSG_TOKEN_EXPIRED_ERROR',
  encryptionKeyChanged = 'MSG_ENCRYPTION_KEY_CHANGED',
  forbidden = 'FORBIDDEN',
}

export enum ErrorStatus {
  unauthorized = 'Non autorisé',
  forbidden = 'Accès interdit',
  internal = 'Erreur interne',
  unknown = 'Erreur inconnue',
  badRequest = 'Requête invalide',
  notFound = 'Ressource introuvable',
  unprocessableEntity = 'Entité non traitable',
}

export enum ErrorCode {
  unauthorized = 401,
  forbidden = 403,
  internal = 500,
  badRequest = 400,
  notFound = 404,
  // 422, Unprocessable Entity
  unprocessableEntity = 422,
}

const localizedErrors: { [key: string]: string } = {
  MSG_TOKEN_EXPIRED_ERROR: 'Votre session a expiré. Veuillez vous reconnecter.',
  MSG_ENCRYPTION_KEY_CHANGED: 'Votre session est invalide. Veuillez vous reconnecter.',
  MSG_ACCESS_DENIED: 'Accès refusé.',
  MSG_DASHBOARD_EXCLUSIVE_RESOURCE_ERROR:
    "Tentative d'accès à une ressource réservée au tableau de bord.",
  MSG_LOGIN_UNAUTHORIZED_ERROR: 'Identifiants invalides.',
  MSG_DEPLOY_NAMESPACE_MISMATCH_ERROR:
    'Impossible de déployer dans un namespace différent de celui sélectionné.',
  MSG_DEPLOY_EMPTY_NAMESPACE_ERROR:
    "Impossible de déployer : le namespace cible n'est pas spécifié.",
};

/**
 * Error returned as a part of backend api calls. All server errors should be in this format.
 */
export class BackendError implements ErrorBase {
  constructor(
    public code: string | number | undefined,
    public status: string | number,
    public message: string,
    public details: any[],
    public type?: string,
    public title?: string,
  ) {
    this.type = 'error';
  }
}

/**
 * Frontend specific errors or errors transformed based on server response.
 */
export class CaaksError implements ErrorBase {
  constructor(
    public code: string | number | undefined,
    public status: string | number,
    public message: string,
    public title?: string,
    public details?: any[],
    public type?: string,
  ) {}

  static isError(error: HttpErrorResponse | AxiosError, ...ApiErrors: string[]): boolean {
    if (axios.isAxiosError(error)) {
      return true;
    } else {
      // API errors will set 'error' as a string.
      if (typeof error.error === 'object') {
        return false;
      }

      for (const apiErr of ApiErrors) {
        if (apiErr === (error.error as string).trim()) {
          return true;
        }
      }

      return false;
    }
  }
}

export function AsCaaksError(error: HttpErrorResponse | AxiosError): CaaksError {
  const result = {} as CaaksError;

  // Handle AxiosError
  if (axios.isAxiosError(error)) {
    result.message = error.message;
    result.code = error.response?.status || 0;
    result.status = error.response?.statusText || 'Unknown error';

    // if (error.response && error.response.data) {
    //   result.message = error.response.data.message || error.message;
    // }

    switch (result.code) {
      case ErrorCode.unauthorized:
        result.status = ErrorStatus.unauthorized;
        break;
      case ErrorCode.forbidden:
        result.status = ErrorStatus.forbidden;
        break;
      case ErrorCode.internal:
        result.status = ErrorStatus.internal;
        break;
      case ErrorCode.notFound:
        result.status = ErrorStatus.notFound;
        break;
      default:
        result.status = ErrorStatus.unknown;
    }

    return new CaaksError(result.status, result.code, result.message, 'Erreur Réseau', []);
  }

  // Handle HttpErrorResponse
  if (error instanceof HttpErrorResponse) {
    result.message = error.message;
    result.code = error.status || 0;

    if (typeof error.error !== 'object') {
      result.message = error.error;
    }

    switch (error.status) {
      case ErrorCode.unauthorized:
        result.status = ErrorStatus.unauthorized;
        break;
      case ErrorCode.forbidden:
        result.status = ErrorStatus.forbidden;
        break;
      case ErrorCode.internal:
        result.status = ErrorStatus.internal;
        break;
      case ErrorCode.notFound:
        result.status = ErrorStatus.notFound;
        break;
      default:
        result.status = ErrorStatus.unknown;
    }
  }
  return result;
  //return new CaaksError(result.status, result.code, result.message, "Erreur Réseau", []);
}

export const ERRORS = {
  forbidden: new CaaksError(
    ErrorStatus.forbidden,
    ErrorCode.forbidden,
    localizedErrors['MSG_ACCESS_DENIED'],
    "Erreur d'autorisation",
    [],
  ),
};
